import { useCallback, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { StudioProvider } from 'Contexts/StudioContext'
import { Studio } from 'Core/Studio'
import Spin from 'antd/lib/spin'
import App from 'antd/lib/app'
import { Loader } from 'Components/common/Loader/Loader'
import { PAGE_TITLES } from 'Constants/page.constants'
import { ContainerType, NotificationPropsType, TaskConfigType } from 'Types/Tasks.types'
import { useFetchAssignment } from 'Hooks/use-assignment'
import { useTitle } from 'Hooks/use-title'
import { useFeatureFlags } from 'Hooks/use-feature-flags'
import { META_PROJECT_ID, NavigationSource } from 'Constants/constants.ts'
import { useQuery } from '@tanstack/react-query'
import { isUserAssociatedWithProject } from 'Services/projects.service'
import { setSendAnalyticsValue } from 'Utils/analytics.utils'
import { useUserProfile } from 'shared/auth/hooks/use-user-profile/useUserProfile'

export const TaskPage = () => {
    const { id, studioConfigData, refetch, isLoading } = useFetchAssignment()
    const { userProfile } = useUserProfile()

    const { data: userAssociationData, isLoading: isUserAssociationDataLoading } = useQuery({
        queryKey: ['userProjectAssociation', META_PROJECT_ID, userProfile.user.user_id],
        queryFn: () =>
            isUserAssociatedWithProject({
                projectId: META_PROJECT_ID,
                userId: userProfile.user.user_id,
            }),
        enabled: !!userProfile.user.user_id,
        staleTime: 3 * 60 * 1000,
    })

    useEffect(() => {
        setSendAnalyticsValue(!userAssociationData?.is_user_associated)
    }, [userAssociationData])

    const {
        data: featureFlagsDta,
        isLoading: isFeatureFlagsLoading,
        error,
    } = useFeatureFlags(
        (studioConfigData?.taskConfig as TaskConfigType<ContainerType, unknown>)?.assignment
            ?.project_id,
        (studioConfigData?.taskConfig as TaskConfigType<ContainerType, unknown>)?.assignment?.step
            ?.id
    )
    const { notification } = App.useApp()
    const navigate = useNavigate()
    const { stepId } = useParams<{ stepId: string }>()

    useTitle(PAGE_TITLES.TASK)

    const onNextTask = useCallback((notifications?: NotificationPropsType) => {
        refetch()
        handleNotifications(notifications)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onExitTask = useCallback(
        (notifications?: NotificationPropsType) => {
            const searchParams = new URLSearchParams(window.location.search)
            const source = searchParams.get('source')

            if (source === NavigationSource.SAVED_TASKS) {
                navigate(`/saved/${stepId}`, { replace: true })
            } else {
                navigate('/', { replace: true })
            }
            handleNotifications(notifications)
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [navigate]
    )

    const handleNotifications = (notifications?: NotificationPropsType) => {
        if (notifications) {
            const { type, message } = notifications
            switch (type) {
                case 'error':
                    notification.error({
                        message,
                        placement: 'bottomLeft',
                    })
                    break
                case 'success':
                    notification.info({
                        message,
                        placement: 'bottomLeft',
                    })
                    break
            }
        }
    }

    if (
        !studioConfigData ||
        isLoading ||
        isFeatureFlagsLoading ||
        error ||
        isUserAssociationDataLoading
    ) {
        return <Spin indicator={<Loader title={'Loading task...'} />} />
    }

    const studio = new Studio({
        ...studioConfigData,
        featureFlags: featureFlagsDta,
        onNextTask,
        onExitTask,
    })

    return (
        <div data-testid="task-wrapper">
            <StudioProvider key={id} value={studio}>
                {studio.workbench.element}
            </StudioProvider>
        </div>
    )
}
